import React from "react";
import { connect } from "react-redux";

import { AppDispatch, IAppState } from "../core/state/Store";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import {
    Alignment,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import Panel from "../core/components/panels/Panel";
import ActivityList from "../core/components/admin/ActivityList";
import UserActivity from "../core/types/admin/UserActivity";
import TTSChart, { TTSChartProps } from "../core/components/charts/Chart";

import "./LoginStats.scss";
import UserActivityLogin from "../core/types/admin/UserActivityLogin";
import Formatting from "../core/foundation/Formatting";
import Utilities from "../core/foundation/Utility";
import Loader from "../core/components/loaders/Loader";
import PanelContainer from "../core/components/panels/PanelContainer";
import List from "../core/components/list/List";
import ProductSubscription from "../core/types/core/ProductSubscription";
import ListItem from "../core/components/list/ListItem";
import { AppContext } from "../core/types/system/AppContext";
import Text from "../core/components/text/Text";
import Button from "../core/components/buttons/Button";

interface ChartData {
    statusConfig: TTSChartProps;
    browserConfig: TTSChartProps;
    platformConfig: TTSChartProps;
    locationConfig: TTSChartProps;
    rawConfig: TTSChartProps;
}

interface RouteLoginStatsProps {
    status: string;
    activity: UserActivity[];
    activityLogins: UserActivityLogin;
    subscriptions: ProductSubscription[];

    setPageLoading: (loading: boolean) => void;
}

interface RouteLoginStatsState {}

class RouteLoginStats extends React.Component<
    RouteLoginStatsProps,
    RouteLoginStatsState
> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state: RouteLoginStatsState = {};

    async componentDidMount() {
        this.props.setPageLoading(true);
        this.props.setPageLoading(false);
    }

    processLoginData(): ChartData {
        let result = {
            statusConfig: {
                labels: [],
                datasets: [
                    {
                        label: "",
                        data: [],
                    },
                ],
            },
            browserConfig: {
                labels: [],
                datasets: [
                    {
                        label: "",
                        data: [],
                    },
                ],
            },
            platformConfig: {
                labels: [],
                datasets: [
                    {
                        label: "",
                        data: [],
                    },
                ],
            },
            locationConfig: {
                labels: [],
                datasets: [
                    {
                        label: "",
                        data: [],
                    },
                ],
            },
            rawConfig: {
                labels: [],
                datasets: [
                    {
                        label: "",
                        data: [],
                        fill: true,
                    },
                ],
            },
        } as ChartData;

        if (this.props.activityLogins) {
            for (let key of Object.keys(this.props.activityLogins.states)) {
                result.statusConfig.labels.push(key);
                result.statusConfig.datasets[0].data.push(
                    this.props.activityLogins.states[key],
                );
            }

            for (let key of Object.keys(this.props.activityLogins.browsers)) {
                result.browserConfig.labels.push(key);
                result.browserConfig.datasets[0].data.push(
                    this.props.activityLogins.browsers[key],
                );
            }

            for (let key of Object.keys(this.props.activityLogins.platforms)) {
                result.platformConfig.labels.push(key);
                result.platformConfig.datasets[0].data.push(
                    this.props.activityLogins.platforms[key],
                );
            }

            for (let key of Object.keys(this.props.activityLogins.locations)) {
                result.locationConfig.labels.push(key);
                result.locationConfig.datasets[0].data.push(
                    this.props.activityLogins.locations[key],
                );
            }

            let today = Formatting.stripTimezone(new Date());
            let startDate = Utilities.dateAddDays(today, -30);

            while (Utilities.dateCompare(startDate, today) <= 0) {
                result.rawConfig.labels.push(Formatting.formatDate(startDate));
                result.rawConfig.datasets[0].data.push(
                    this.props.activityLogins.logged[
                        startDate.toISOString().replace(".000Z", "")
                    ] || 0,
                );
                startDate = Utilities.dateAddDays(startDate, 1);
            }
        }

        return result;
    }

    render() {
        const chartData = this.processLoginData();
        return (
            <section className="full-height">
                <GeneralContainer
                    scaling={true}
                    max={true}
                    alignment={Alignment.Center}
                    clearanceSide={Side.Top}
                    bufferSide={Side.Bottom}>
                    <PanelContainer>
                        <Panel
                            title={`User Sessions (${
                                this.props.activity?.length || 0
                            })`}
                            size="large"
                            containContent={true}>
                            <ActivityList />
                        </Panel>
                        <Panel
                            title="Logins (Results Last 30 Days)"
                            size="large"
                            chartContent={true}>
                            {this.props.status === "succeeded" ? (
                                chartData.statusConfig.datasets && (
                                    <TTSChart
                                        type="Doughnut"
                                        labels={chartData.statusConfig.labels}
                                        datasets={
                                            chartData.statusConfig.datasets
                                        }
                                        maxHeight="220px"
                                    />
                                )
                            ) : (
                                <Loader />
                            )}
                        </Panel>
                        <Panel
                            title="Logins (Browser Last 30 Days)"
                            size="large"
                            chartContent={true}>
                            {this.props.status === "succeeded" ? (
                                chartData.browserConfig.datasets && (
                                    <TTSChart
                                        type="Bar"
                                        labels={chartData.browserConfig.labels}
                                        datasets={
                                            chartData.browserConfig.datasets
                                        }
                                        maxHeight="220px"
                                    />
                                )
                            ) : (
                                <Loader />
                            )}
                        </Panel>
                        <Panel
                            title="Logins (Platform Last 30 Days)"
                            size="large"
                            chartContent={true}>
                            {this.props.status === "succeeded" ? (
                                chartData.platformConfig.datasets && (
                                    <TTSChart
                                        type="Doughnut"
                                        labels={chartData.platformConfig.labels}
                                        datasets={
                                            chartData.platformConfig.datasets
                                        }
                                        maxHeight="220px"
                                    />
                                )
                            ) : (
                                <Loader />
                            )}
                        </Panel>
                        <Panel
                            title="Logins (Location Last 30 Days)"
                            size="large"
                            chartContent={true}>
                            {this.props.status === "succeeded" ? (
                                chartData.locationConfig.datasets && (
                                    <TTSChart
                                        type="Bar"
                                        labels={chartData.locationConfig.labels}
                                        datasets={
                                            chartData.locationConfig.datasets
                                        }
                                        maxHeight="220px"
                                    />
                                )
                            ) : (
                                <Loader />
                            )}
                        </Panel>
                        <Panel
                            title="Logins (Last 30 Days)"
                            size="large"
                            chartContent={true}>
                            {this.props.status === "succeeded" ? (
                                chartData.rawConfig.datasets && (
                                    <TTSChart
                                        type="Line"
                                        labels={chartData.rawConfig.labels}
                                        datasets={chartData.rawConfig.datasets}
                                        maxHeight="220px"
                                    />
                                )
                            ) : (
                                <Loader />
                            )}
                        </Panel>
                    </PanelContainer>

                    <PanelContainer>
                        <Panel
                            title="Product Subscriptions"
                            size="large">
                            <List joined={true}>
                                {this.props.subscriptions.map(
                                    (subscription, index) => (
                                        <ListItem
                                            key={index}
                                            autoHeight={true}>
                                            <GeneralContainer
                                                flex={FlexDirection.Column}
                                                bufferSide={Side.All}>
                                                <h4>
                                                    {
                                                        subscription.clientSystemName
                                                    }
                                                </h4>
                                                {subscription.licenceCode && (
                                                    <Text
                                                        inline={true}
                                                        clearanceSide={Side.Top}
                                                        clearanceSize={
                                                            Size.Small
                                                        }>
                                                        <b>Licence Code:</b>
                                                        {
                                                            subscription.licenceCode
                                                        }
                                                    </Text>
                                                )}
                                            </GeneralContainer>
                                            <GeneralContainer
                                                flex={FlexDirection.Column}
                                                bufferSide={Side.All}
                                                alignment={Alignment.Right}>
                                                <Text>
                                                    <b>Expires:</b>
                                                    {subscription.expiryDate
                                                        .toDate()
                                                        .toLocale()}
                                                </Text>

                                                {subscription.clientSystemName ===
                                                "Desktop Client V10" ? (
                                                    <Button
                                                        icon="open_in_new"
                                                        inline={true}
                                                        transparent={true}
                                                        align={Alignment.Right}
                                                        clearanceSide={Side.Top}
                                                        clearanceSize={
                                                            Size.Small
                                                        }
                                                        onClick={() =>
                                                            this.context.navigate(
                                                                "/version10",
                                                            )
                                                        }
                                                        tooltip="Open Desktop Client V10 Product Page"
                                                    />
                                                ) : subscription.clientSystemName ===
                                                  "Desktop Client V9" ? (
                                                    <Button
                                                        icon="open_in_new"
                                                        inline={true}
                                                        transparent={true}
                                                        align={Alignment.Right}
                                                        clearanceSide={Side.Top}
                                                        clearanceSize={
                                                            Size.Small
                                                        }
                                                        onClick={() =>
                                                            this.context.navigate(
                                                                "/version9",
                                                            )
                                                        }
                                                        tooltip="Open Desktop Client V9 Product Page"
                                                    />
                                                ) : null}
                                            </GeneralContainer>
                                        </ListItem>
                                    ),
                                )}
                            </List>
                        </Panel>
                    </PanelContainer>
                </GeneralContainer>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { activity, productSubscription } = state.db;
    return {
        status: activity.loginsStatus,
        activity: activity.activity,
        activityLogins: activity.activityLogins,
        subscriptions: productSubscription.productSubscriptions,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteLoginStats);
